import { toQueryString } from '@utils/requests';

import { NotificationSettings, NotificationType } from 'types/notifications';

import { request, unhandledRequest } from '../request';

export async function loadSettings() {
  const response = await unhandledRequest.get<Array<NotificationSettings>>({
    url: `/notificationsettings/`,
  });
  return response.data.reduce(
    (settings, item) => {
      settings[item.notificationType] = {
        notifyInApp: item.notifyInApp,
        notifyByEmail: item.notifyByEmail,
      };
      return settings;
    },
    {} as { [K in NotificationType]: Omit<NotificationSettings, 'notificationType'> },
  );
}

export function loadUser() {
  return request.get({ url: '/user/' });
}

export function enableInAppNotificationSetting(notificationType: NotificationType) {
  return unhandledRequest.post({ url: `/notifications/settings/app/${notificationType}/` });
}

export function disableInAppNotificationSetting(notificationType: NotificationType) {
  return unhandledRequest.delete({ url: `/notifications/settings/app/${notificationType}/` });
}
export function enableMailNotificationSetting(notificationType: NotificationType) {
  return unhandledRequest.post({ url: `/notifications/settings/mail/${notificationType}/` });
}

export function disableMailNotificationSetting(notificationType: NotificationType) {
  return unhandledRequest.delete({ url: `/notifications/settings/mail/${notificationType}/` });
}

export function markAsRead(notificationId: number) {
  return request.patch({
    url: `/notifications/${notificationId}/`,
    data: { isRead: true },
  });
}

export function markAsUnread(notificationId: number) {
  return request.patch({
    url: `/notifications/${notificationId}/`,
    data: { isRead: false },
  });
}

export function archive(notificationId: number) {
  return request.patch({
    url: `/notifications/${notificationId}/`,
    data: { isRead: true, isArchived: true },
  });
}

export function unArchive(notificationId: number) {
  return request.patch({
    url: `/notifications/${notificationId}/`,
    data: { isArchived: false },
  });
}

export function markAllAsRead(latestLoadedNotificationId: number) {
  return unhandledRequest.post({
    url: `/notifications/read/${toQueryString({ latest: latestLoadedNotificationId })}`,
  });
}

export function archiveAll(latestLoadedNotificationId: number) {
  return unhandledRequest.post({
    url: `/notifications/archive/${toQueryString({ latest: latestLoadedNotificationId })}`,
  });
}

export function load(lastChecked: string) {
  const query = toQueryString({
    limit: 100,
    ordering: '-created',
    created__gt: lastChecked || null,
  });

  return request.get({
    url: '/notifications/' + query,
  });
}

export type NotificationList = {
  count: number;
  next?: string;
  previous?: string;
  results: Notification[];
};

export type NotificationBase = {
  after: null;
  applicationId: number;
  applicationIds: number[];
  applicationName: string;
  before: null;
  created: string;
  id: number;
  isArchived: boolean;
  isRead: boolean;
  jobId: number;
  jobTitle: string;
  notificationType: Exclude<
    NotificationType,
    'ADD_JOB_NOTE' | 'EXPIRE_JOB' | 'INTEGRATION' | 'MOVE_STAGE'
  >;
  objectId: number | null;
  triggerId: null;
  triggerType: null;
  user: number | null;
};

type NotificationAddJobNoteType = Omit<NotificationBase, 'applicationId' | 'notificationType'> & {
  applicationId: null;
  notificationType: 'ADD_JOB_NOTE';
};

type NotificationExpireJobType = Omit<NotificationBase, 'applicationId' | 'notificationType'> & {
  applicationId: null;
  notificationType: 'EXPIRE_JOB';
};

type NotificationIntegrationType = Omit<
  NotificationBase,
  'applicationId' | 'notificationType' | 'triggerId' | 'triggerType'
> & {
  applicationId: number;
  notificationType: 'INTEGRATION';
  triggerId: number;
  triggerType: string;
};

type NotificationMoveStageType = Omit<NotificationBase, 'after' | 'before' | 'notificationType'> & {
  after: {
    name: string;
    order: number;
    pk: number;
  } | null;
  before: {
    name: string;
    order: number;
    pk: number;
  } | null;
  notificationType: 'MOVE_STAGE';
};

export type Notification =
  | NotificationBase
  | NotificationAddJobNoteType
  | NotificationExpireJobType
  | NotificationIntegrationType
  | NotificationMoveStageType;

export type NotificationSection = 'activity' | 'archive';

type ListProps = {
  offset?: number;
  pageSize?: number;
  unread?: boolean;
  section: NotificationSection;
  newApplication?: boolean;
  assignedToMe?: boolean;
  mentions?: boolean;
  searchValue?: string;
};

export const list = async (props: ListProps) => {
  const query = toQueryString({
    limit: props.pageSize ?? 50,
    offset: props.offset ?? 0,
    ordering: '-created',
    is_archived: props.section === 'archive',
    ...(props.searchValue && { search: props.searchValue }),
    ...(props.unread && { is_read: false }),
    ...(props.newApplication && { notification_type: 'APPLY_JOB' }),
    ...(props.assignedToMe && { notification_type: 'APPLICANT_ASSIGNED' }),
  });

  const response = await unhandledRequest.get<NotificationList>({
    url: '/notifications/' + query,
  });
  return response.data;
};

export type CountResponse = {
  unread: number;
  newApplications: number;
  assigned: number;
  total: number;
  latestId?: number;
};

export const counts = async () => {
  const response = await unhandledRequest.get<CountResponse>({
    url: '/notifications/counts/?is_archived=false',
  });
  return response.data;
};
